import {
  SET_USER_LOGGED_IN,
  PARTNER_REGISTRATION_EMAIL_INVALID,
  PARTNER_REGISTRATION_REGISTERED_COMPANIES,
  PARTNER_REGISTRATION_STATUS,
  LOAD_COMMON_HEADERS,
  SET_ERROR_CODE,
  SET_IMS_PROFILE_EXISTS,
  SET_PARTNER_REGISTRATION_FORM_STEP,
  SET_SELECTED_REGISTER_COMPANY_TYPE,
  SET_DXP_USER_PROFILE_DRAFT,
} from "./actionTypes";

/**
 * Full page reload (F5 / refresh). Used so Company Information (step 4) cannot be
 * re-entered after refresh — user is sent back to User profile (step 3), per registration UX spec.
 */
function isBrowserReload(): boolean {
  if (typeof performance === "undefined") return false;
  const entries = performance.getEntriesByType(
    "navigation",
  ) as PerformanceNavigationTiming[];
  const nav = entries[0];
  if (nav?.type === "reload") return true;
  const legacy = performance as unknown as { navigation?: { type?: number } };
  return legacy.navigation?.type === 1;
}

// Initialize partnerRegistrationFormStep from localStorage if available
const getInitialFormStep = (): number => {
  const raw = localStorage.getItem("partnerRegistrationFormStep");
  const parsed = raw ? parseInt(raw, 10) : 0;
  const step = Number.isNaN(parsed) ? 0 : parsed;
  if (step === 4 && isBrowserReload()) {
    localStorage.setItem("partnerRegistrationFormStep", "3");
    return 3;
  }
  return step;
};

const REGISTRATION_RESPONSE = {
  registeredCompanies: [] as unknown[],
  isInvalidEmail: false,
  partnerRegistrationFormStep: getInitialFormStep(),
  isCompanyPage: false,
  partnerRegistrationStatus: "NotRegistered",
  commonHeaders: {},
  isServerError: false,
  programType: "",
  isUserLoggedIn: false,
  /** Set from API (email check); default false so "Create Adobe ID" / signUp path is correct after refresh. */
  imsProfileExists: false,
  selectedRegisterCompanyType: null as string | null, // set when user selects company type
  /** Personal profile from step 3 (Next) before company step / final save. */
  dxpUserProfileDraft: null as Record<string, unknown> | null,
};

interface ActionType {
  type: string;
  payload?: {
    isInvalidEmail?: boolean;
    currentStep?: number;
    registeredCompanies?: unknown[];
    stepNumber?: number;
    status?: string;
    data?: Record<string, unknown>;
    loginStatus?: boolean;
    imsProfileExists?: boolean;
    companyType?: string | null;
    draft?: Record<string, unknown> | null;
  };
}

export type RegistrationState = typeof REGISTRATION_RESPONSE;

export default (
  state = REGISTRATION_RESPONSE,
  action: ActionType,
): RegistrationState => {
  switch (action?.type) {
    case PARTNER_REGISTRATION_EMAIL_INVALID:
      return {
        ...state,
        registeredCompanies: [],
        isInvalidEmail: action.payload?.isInvalidEmail ?? false,
        isCompanyPage: false,
      };
    case PARTNER_REGISTRATION_REGISTERED_COMPANIES:
      if (
        action.payload?.currentStep !== undefined &&
        action.payload?.currentStep !== null
      ) {
        localStorage.setItem(
          "partnerRegistrationFormStep",
          String(action.payload.currentStep),
        );
      }
      return {
        ...state,
        isInvalidEmail: false,
        registeredCompanies: action.payload?.registeredCompanies ?? [],
        partnerRegistrationFormStep: action.payload?.currentStep ?? 0,
        isCompanyPage: action.payload?.currentStep === 1,
      };

    case PARTNER_REGISTRATION_STATUS:
      if (
        action.payload?.stepNumber !== undefined &&
        action.payload?.stepNumber !== null
      ) {
        localStorage.setItem(
          "partnerRegistrationFormStep",
          String(action.payload.stepNumber),
        );
      }
      return {
        ...state,
        partnerRegistrationFormStep: action.payload?.stepNumber ?? 0,
        partnerRegistrationStatus: action.payload?.status ?? "NotRegistered",
      };
    case LOAD_COMMON_HEADERS:
      return {
        ...state,
        commonHeaders: action.payload?.data ?? {},
        isServerError: false,
      };
    case SET_ERROR_CODE:
      return {
        ...state,
        isServerError: true,
      };

    case SET_USER_LOGGED_IN:
      return {
        ...state,
        isUserLoggedIn: action.payload?.loginStatus ?? false,
      };

    case SET_IMS_PROFILE_EXISTS:
      return {
        ...state,
        imsProfileExists: !!action.payload?.imsProfileExists,
      };

    case SET_PARTNER_REGISTRATION_FORM_STEP:
      if (action.payload?.stepNumber !== undefined) {
        localStorage.setItem(
          "partnerRegistrationFormStep",
          String(action.payload.stepNumber),
        );
      }
      return {
        ...state,
        partnerRegistrationFormStep: action.payload?.stepNumber ?? 0,
      };

    case SET_SELECTED_REGISTER_COMPANY_TYPE:
      return {
        ...state,
        selectedRegisterCompanyType: action.payload?.companyType ?? null,
      };

    case SET_DXP_USER_PROFILE_DRAFT:
      return {
        ...state,
        dxpUserProfileDraft:
          action.payload?.draft === undefined
            ? state.dxpUserProfileDraft
            : action.payload.draft,
      };

    default:
      return { ...state };
  }
};
